import { useState, useEffect, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

// react router
import { Link, useNavigate } from "react-router-dom";

// form validation
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { _register, _checkPageAccess } from "../../redux/actions";

// utilities
import { isEmpty } from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";
import { showErrorSnackbar } from "../../../snackbar/helpers";

// redux
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../../redux/slices/userDataSlice";
import { setBackdropOpen } from "../../../../redux/slices/backdropSlice";

// components
import "react-toastify/dist/ReactToastify.css";
import { Form } from "reactstrap";
import PasswordRequirements from "./PasswordRequirements";
import Honeypot from "../../../../components/Honeypot";
import WealthTechLogoLoader from "../../../../components/WealthTechLogoLoader/WealthTechLogoLoader";
import { setInformationModalData, setInformationModalOpen } from "../../../modals/information/redux/informationModalSlice";

// Fallback values for environment variables
const SITE_KEY = process.env.REACT_APP_SITE_KEY || '6LeFxpEpAAAAADwQ0FRqy_ukCnYxtA23xqgxm-oW' // Default fallback, will be replaced by actual env var in production
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-ffffffffffff' // Test key

// register schema
const registerSchema = Yup.object().shape({
  // username: Yup.string()
  //   .required("Username is required")
  //   .min(4, "The username must be 4-12 characters long")
  //   .max(12, "The username must be 4-12 characters long")
  //   .matches(/^\S*$/, "The username cannot contain any spaces")
  //   .matches(
  //     /^'?\p{L}+(?:[' ]\p{L}+)*'?$/u,
  //     "The username cannot contain spacial characters"
  //   ),
  firstName: Yup.string()
    .required("First name is required")
    .min(2, "First name must be alteast 2 characters")
    .matches(
      /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
      "First name can only contain letters."
    ),
  lastName: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be alteast 2 characters")
    .matches(
      /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
      "Last name can only contain letters."
    ),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/,
      "Password doesn't meet requirements"
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Password confirmation is required"),
  // termsAndConditions: Yup.boolean()
  //   .oneOf([true], "You must accept the terms and conditions")
  //   .required("You must accept the terms and conditions")
})

const SignupTab = () => {
  // ** States
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [passwordsMatch, setPasswordsMatch] = useState(true)
  const [loading, setLoading] = useState("")
  const [recaptchaResponse, setRecaptchaResponse] = useState("")
  const [recaptchaConfirmed, setRecaptchaConfirmed] = useState(false)
  const [termsConfirmed, setTermsConfirmed] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordFieldFocused, setPasswordFieldFocused] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480)
  const [pendingData, setPendingData] = useState(null)
  const [pageAccessAllowed, setPageAccessAllowed] = useState(false)
  const [pageAccessLoading, setPageAccessLoading] = useState(true)
  const [pageAccessError, setPageAccessError] = useState(null)
  const captchaRef = useRef(null)
  const theme = useSelector((state) => state.theme.value)
  const isLoggedIn = useSelector((state) => state.userData.value.email)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    resolver: yupResolver(registerSchema),
    mode: "onChange" // Changed from onSubmit to onChange for real-time validation
  })

  // Watch the password and confirmPassword fields
  const watchPassword = watch("password")
  const watchConfirmPassword = watch("confirmPassword")

  // Check if passwords match when either field changes
  useEffect(() => {
    if (watchConfirmPassword && watchPassword !== watchConfirmPassword) {
      setPasswordsMatch(false)
    } else {
      setPasswordsMatch(true)
    }
  }, [watchPassword, watchConfirmPassword])

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const onHCaptchaVerify = (value) => {
    setRecaptchaConfirmed(!!value)
    setRecaptchaResponse(value)
    if (pendingData) {
      performRegister(pendingData, value)
    }
  }

  const onHCaptchaError = (error) => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setLoading(false)
    dispatch(setBackdropOpen(false))
  }

  const onHCaptchaExpire = () => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
  }

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword)
    } else {
      setShowConfirmPassword(!showConfirmPassword)
    }
  }

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width <= 768)
      setIsSmallMobile(width <= 480)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/trade")
    }


    setPageAccessLoading(true)
    _checkPageAccess(
      'register',
      (data) => {
        setPageAccessAllowed(true)
        setPageAccessLoading(false)
        setPageAccessError(null)
      },
      (error) => {
        setPageAccessLoading(false)
        if (error?.data?.error === 'rate_limit_exceeded') {
          setPageAccessAllowed(false)
          setPageAccessError(error.data.message || 'Too many requests. Please try again later.')
        } else {
          setPageAccessAllowed(false)
          setPageAccessError(error?.data?.message || 'Unable to verify page access.')
        }
      }
    )
  }, [])
  const exchangeName = useSelector((state) => state.exchangeCustomization.value.name)

  document.title = `${exchangeName}: Register`

  const onSubmit = (data) => {
    if (data.company_name) {
      return
    }

    if (!pageAccessAllowed) {
      showErrorSnackbar({
        alertMessage: pageAccessError || 'Too many requests. Please try again later.'
      })
      return
    }

    if (isEmpty(errors)) {
      setLoading(true)
      dispatch(setBackdropOpen(true))
      setPendingData(data)
      if (captchaRef.current) {
        captchaRef.current.execute()
      } else {
        performRegister(data, "")
      }
    }
  }

  const performRegister = (data, captchaToken) => {
    _register(
      {
        // username: data.username,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        terms: termsConfirmed,
        captcha_response: captchaToken
      },
      (data) => {
        console.log(data)
        // before
        dispatch(setBackdropOpen(false))

        dispatch(setUser(data))
        // navigate("/trade")

        // after
        // console.log("User registered successfully - 1")
        navigate('/trade')
        dispatch(setInformationModalData({
          title: 'Verification Sent',
          subtitle: 'An email was sent to verify your email address. Please check your inbox and click the link to verify your email address.',
        }))
        dispatch(setInformationModalOpen(true))
        // navigate('/email-verification-required')
        // console.log("User registered successfully - 2")
        setLoading(false)
        setPendingData(null)
      },
      (error) => {
        console.log(error)
        setLoading(false)
        setPendingData(null)
        dispatch(setBackdropOpen(false))

        showErrorSnackbar({
          alertMessage: error.errors[0].length ? (
            <FormattedMessage id={error.errors[0]} />
          ) : (
            "Unknown Error"
          )
        })
      }
    )
  }


  if (pageAccessLoading) {
    return (
      <div style={{ width: '100%', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <WealthTechLogoLoader />
      </div>
    )
  }

  if (!pageAccessAllowed && pageAccessError) {
    return (
      <div style={{ width: '100%', minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <h2 style={{ color: '#ef4444', marginBottom: '10px' }}>Access Unavailable</h2>
        <p style={{ color: 'var(--text-secondary, #94a3b8)' }}>{pageAccessError}</p>
      </div>
    )
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Mobile Logo/Branding */}
      {isMobile && (
        <div style={{
          textAlign: 'center',
          marginBottom: isSmallMobile ? '30px' : '40px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isSmallMobile ? '8px' : '12px',
            marginBottom: isSmallMobile ? '20px' : '25px',
          }}>
            <img
              src="/logo-small-white.svg"
              alt="Logo"
              width={isSmallMobile ? "28" : "35"}
              height={isSmallMobile ? "28" : "35"}
              style={{ objectFit: 'contain' }}
            />
            <span style={{
              fontSize: isSmallMobile ? '24px' : '28px',
              fontWeight: 700,
              color: 'var(--text-color, white)',
            }}>WealthTech</span>
          </div>
          <p style={{
            color: 'var(--text-secondary, #94a3b8)',
            fontSize: isSmallMobile ? '14px' : '16px',
            lineHeight: 1.5,
            margin: 0,
          }}>
            Join thousands of traders who trust WealthTech for secure crypto trading.
          </p>
        </div>
      )}

      <div style={{ marginBottom: isSmallMobile ? '20px' : isMobile ? '25px' : '30px' }}>
        <h2 style={{
          fontSize: isSmallMobile ? '24px' : isMobile ? '26px' : '32px',
          color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
          marginBottom: '10px',
        }}>Create Account</h2>
        <p style={{
          color: 'var(--text-secondary, #94a3b8)',
          fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '16px',
        }}>Fill in your details to get started</p>

        {pageAccessError && (
          <div style={{
            padding: '10px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#ef4444',
            marginTop: '10px',
            fontSize: '14px'
          }}>
            {pageAccessError}
          </div>
        )}
      </div>

      {/* form */}
      <Form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>

        {/* username input */}
        {/* <Input
          id="username"
          inputLabel="Username"
          error={errors.username}
          {...register("username")}
        /> */}

        {/* Names Input Container */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '15px',
          marginBottom: isSmallMobile ? '20px' : '25px',
        }}>
          {/* First Name input */}
          <div>
            <label htmlFor="firstName" style={{
              display: 'block',
              color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
              fontSize: isSmallMobile ? '13px' : '14px',
              fontWeight: 500,
              marginBottom: '8px',
            }}>First Name</label>
            <input
              type="text"
              id="firstName"
              placeholder="John"
              {...register("firstName")}
              style={{
                width: '100%',
                padding: isSmallMobile ? '10px 12px' : isMobile ? '12px 14px' : '14px 16px',
                background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
                border: `2px solid ${errors.firstName ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
                borderRadius: '10px',
                color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
                fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              required
            />
            {errors.firstName && (
              <span style={{ color: '#ef4444', fontSize: isSmallMobile ? '11px' : '12px', marginTop: '4px', display: 'block' }}>
                {errors.firstName.message}
              </span>
            )}
          </div>

          {/* Last Name input */}
          <div>
            <label htmlFor="lastName" style={{
              display: 'block',
              color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
              fontSize: isSmallMobile ? '13px' : '14px',
              fontWeight: 500,
              marginBottom: '8px',
            }}>Last Name</label>
            <input
              type="text"
              id="lastName"
              placeholder="Doe"
              {...register("lastName")}
              style={{
                width: '100%',
                padding: isSmallMobile ? '10px 12px' : isMobile ? '12px 14px' : '14px 16px',
                background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
                border: `2px solid ${errors.lastName ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
                borderRadius: '10px',
                color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
                fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              required
            />
            {errors.lastName && (
              <span style={{ color: '#ef4444', fontSize: isSmallMobile ? '11px' : '12px', marginTop: '4px', display: 'block' }}>
                {errors.lastName.message}
              </span>
            )}
          </div>
        </div>


        {/* Email input */}
        <div style={{ marginBottom: isSmallMobile ? '20px' : '25px' }}>
          <label htmlFor="email" style={{
            display: 'block',
            color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
            fontSize: isSmallMobile ? '13px' : '14px',
            fontWeight: 500,
            marginBottom: '8px',
          }}>Email</label>
          <input
            type="email"
            id="email"
            placeholder="john.doe@example.com"
            {...register("email")}
            style={{
              width: '100%',
              padding: isSmallMobile ? '10px 12px' : isMobile ? '12px 14px' : '14px 16px',
              background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
              border: `2px solid ${errors.email ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
              borderRadius: '10px',
              color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
              fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
              boxSizing: 'border-box',
              outline: 'none',
            }}
            required
          />
          {errors.email && (
            <span style={{ color: '#ef4444', fontSize: isSmallMobile ? '11px' : '12px', marginTop: '4px', display: 'block' }}>
              {errors.email.message}
            </span>
          )}
        </div>

        {/* Password input */}
        <div style={{ marginBottom: isSmallMobile ? '15px' : '20px' }}>
          <label htmlFor="password" style={{
            display: 'block',
            color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
            fontSize: isSmallMobile ? '13px' : '14px',
            fontWeight: 500,
            marginBottom: '8px',
          }}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              placeholder="••••••••••••"
              {...register("password")}
              onChange={(e) => {
                register("password").onChange(e)
                setPassword(e.target.value)
              }}
              onFocus={() => setPasswordFieldFocused(true)}
              onBlur={() => setPasswordFieldFocused(false)}
              style={{
                width: '100%',
                padding: isSmallMobile ? '10px 35px 10px 12px' : isMobile ? '12px 40px 12px 14px' : '14px 45px 14px 16px',
                background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
                border: `2px solid ${errors.password ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
                borderRadius: '10px',
                color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
                fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              required
            />
            <span
              onClick={() => togglePasswordVisibility('password')}
              style={{
                position: 'absolute',
                right: isSmallMobile ? '12px' : '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-secondary, #64748b)',
                cursor: 'pointer',
              }}
            >
              <svg
                width={isSmallMobile ? "18" : "20"}
                height={isSmallMobile ? "18" : "20"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {showPassword ? (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </>
                ) : (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </>
                )}
              </svg>
            </span>
          </div>
          {errors.password && (
            <span style={{ color: '#ef4444', fontSize: isSmallMobile ? '11px' : '12px', marginTop: '4px', display: 'block' }}>
              {errors.password.message}
            </span>
          )}
        </div>

        {/* Password Requirements - Only show when password field is focused */}
        {passwordFieldFocused && (
          <PasswordRequirements
            password={password}
            containerStyle={{
              marginBottom: isSmallMobile ? 15 : 18,
              marginTop: -5,
              padding: isSmallMobile ? '10px' : '12px',
              background: 'var(--input-background, rgba(30, 50, 70, 0.3))',
              borderRadius: '8px',
              border: '1px solid var(--input-border, rgba(100, 150, 180, 0.2))'
            }}
          />
        )}

        {/* Confirm Password input */}
        <div style={{ marginBottom: isSmallMobile ? '20px' : '25px' }}>
          <label htmlFor="confirmPassword" style={{
            display: 'block',
            color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
            fontSize: isSmallMobile ? '13px' : '14px',
            fontWeight: 500,
            marginBottom: '8px',
          }}>Confirm Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              placeholder="••••••••••••"
              {...register("confirmPassword")}
              onChange={(e) => {
                register("confirmPassword").onChange(e)
                setConfirmPassword(e.target.value)
              }}
              style={{
                width: '100%',
                padding: isSmallMobile ? '10px 35px 10px 12px' : isMobile ? '12px 40px 12px 14px' : '14px 45px 14px 16px',
                background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
                border: `2px solid ${(errors.confirmPassword || !passwordsMatch) ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
                borderRadius: '10px',
                color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
                fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              required
            />
            <span
              onClick={() => togglePasswordVisibility('confirmPassword')}
              style={{
                position: 'absolute',
                right: isSmallMobile ? '12px' : '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-secondary, #64748b)',
                cursor: 'pointer',
              }}
            >
              <svg
                width={isSmallMobile ? "18" : "20"}
                height={isSmallMobile ? "18" : "20"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {showConfirmPassword ? (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </>
                ) : (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </>
                )}
              </svg>
            </span>
          </div>
          {(errors.confirmPassword || (!passwordsMatch && confirmPassword)) && (
            <span style={{ color: '#ef4444', fontSize: isSmallMobile ? '11px' : '12px', marginTop: '4px', display: 'block' }}>
              {errors.confirmPassword?.message || "Passwords don't match"}
            </span>
          )}
        </div>

        {/* Terms and Conditions */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: isSmallMobile ? '8px' : '10px',
          margin: isSmallMobile ? '15px 0' : '18px 0',
          padding: isSmallMobile ? '10px' : '12px',
          background: 'var(--input-background, rgba(30, 50, 70, 0.3))',
          borderRadius: '8px',
          border: '1px solid var(--input-border, rgba(100, 150, 180, 0.3))',
        }}>
          <input
            type="checkbox"
            id="terms"
            checked={termsConfirmed}
            onChange={(e) => setTermsConfirmed(e.target.checked)}
            style={{
              width: isSmallMobile ? '16px' : '18px',
              height: isSmallMobile ? '16px' : '18px',
              cursor: 'pointer',
              marginTop: '2px',
              accentColor: '#009999',
              flexShrink: 0,
            }}
            required
          />
          <label htmlFor="terms" style={{
            margin: 0,
            cursor: 'pointer',
            color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
            fontSize: isSmallMobile ? '11px' : isMobile ? '12px' : '13px',
            lineHeight: 1.4,
          }}>
            By Register I agree that I'm 18 years of age or older, of the{' '}
            <Link
              to="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#009999',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              <FormattedMessage id="page.signUp.termsAndService.text" />
            </Link>
            {", "}
            <Link
              to="/privacy-and-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#009999',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              <FormattedMessage id="page.signUp.PrivacyPolicy.text" />
            </Link>
          </label>
        </div>

        {/* Honeypot */}
        <Honeypot register={register} />

        {/* hCaptcha (Invisible) */}
        <HCaptcha
          id="hcaptcha-signup"
          ref={captchaRef}
          size="invisible"
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={onHCaptchaVerify}
          onError={onHCaptchaError}
          onExpire={onHCaptchaExpire}
          theme={theme}
        />

        {/* Register button */}
        <button
          type="submit"
          disabled={loading || !termsConfirmed || !passwordsMatch || !pageAccessAllowed}
          style={{
            width: '100%',
            padding: isSmallMobile ? '12px' : isMobile ? '14px' : '16px',
            background: (loading || !termsConfirmed || !passwordsMatch || !pageAccessAllowed) ? 'var(--button-disabled, #6b7280)' : 'linear-gradient(135deg, #009999 0%, #007a7a 100%)',
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            fontSize: isSmallMobile ? '14px' : '16px',
            fontWeight: 600,
            cursor: (loading || !termsConfirmed || !passwordsMatch || !pageAccessAllowed) ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 15px rgba(0, 153, 153, 0.3)',
            opacity: (loading || !termsConfirmed || !passwordsMatch || !pageAccessAllowed) ? 0.7 : 1,
          }}
        >
          {loading ? 'Processing...' : <FormattedMessage id="page.signIn.signup" />}
        </button>

        {/* Login link */}
        <div style={{
          textAlign: 'center',
          color: 'var(--text-secondary, #94a3b8)',
          fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
          marginTop: isSmallMobile ? '20px' : isMobile ? '25px' : '30px',
        }}>
          <FormattedMessage id="page.signUp.signinExists" />{' '}
          <Link to="/login" style={{
            color: '#009999',
            textDecoration: 'none',
            fontWeight: 600,
          }}>
            <FormattedMessage id="page.signUp.signin" />
          </Link>
        </div>
      </Form>
    </div>
  )
}
export default injectIntl(SignupTab)
